import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { myDriveAssessment, startDriveAssessment, driveAssessmentSnapshot, submitDriveAssessment } from "@/lib/campusApi";
import {
  ShieldCheck, ShieldAlert, Loader2, Camera, CameraOff, Clock, CheckCircle2,
  Lock, Maximize, AlertTriangle, ArrowLeft,
} from "lucide-react";

/* Drive assessment runner — the college's REAL screening test.
   Mettl-grade guardrails (browser-level):
     · camera REQUIRED to start; webcam snapshot every ~30s → server
     · fullscreen enforced; exits are recorded violations
     · tab blur / copy / paste / right-click blocked where possible & recorded
     · one attempt, server-side window & timer, results only when TPO publishes */

const SNAPSHOT_EVERY_MS = 30_000;

export default function DriveAssessmentPage() {
  const { driveId } = useParams();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [stage, setStage] = useState("loading"); // loading | intro | live | submitting | done | error
  const [test, setTest] = useState(null);
  const [answers, setAnswers] = useState({});
  const [err, setErr] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [doneMsg, setDoneMsg] = useState("");

  // Proctoring state
  const [cameraActive, setCameraActive] = useState(false);
  const [violations, setViolations] = useState(0);
  const webcamRef = useRef(null);
  const streamRef = useRef(null);
  const eventsRef = useRef([]);
  const stageRef = useRef("loading");
  const snapTimerRef = useRef(null);
  useEffect(() => { stageRef.current = stage; }, [stage]);

  const recordEvent = useCallback((type) => {
    if (stageRef.current !== "live") return;
    eventsRef.current.push({ t: new Date().toISOString(), type });
    setViolations(eventsRef.current.length);
  }, []);

  useEffect(() => {
    myDriveAssessment(driveId)
      .then(({ data }) => { setMeta(data); setStage(data.status === "open" ? "intro" : "done"); if (data.status !== "open") setDoneMsg(statusMessage(data)); })
      .catch((e) => { setErr(e?.response?.data?.detail || "Could not load the assessment."); setStage("error"); });
    return () => cleanup(); // eslint-disable-line
  }, [driveId]); // eslint-disable-line

  // ── Violation listeners (attached only while live) ──
  useEffect(() => {
    if (stage !== "live") return;
    const onVis = () => { if (document.hidden) recordEvent("tab_blur"); };
    const onFs = () => { if (!document.fullscreenElement) recordEvent("fullscreen_exit"); };
    const onCopy = (e) => { e.preventDefault(); recordEvent("copy"); };
    const onPaste = (e) => { e.preventDefault(); recordEvent("paste"); };
    const onCtx = (e) => { e.preventDefault(); recordEvent("rightclick"); };
    document.addEventListener("visibilitychange", onVis);
    document.addEventListener("fullscreenchange", onFs);
    document.addEventListener("copy", onCopy);
    document.addEventListener("paste", onPaste);
    document.addEventListener("contextmenu", onCtx);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      document.removeEventListener("fullscreenchange", onFs);
      document.removeEventListener("copy", onCopy);
      document.removeEventListener("paste", onPaste);
      document.removeEventListener("contextmenu", onCtx);
    };
  }, [stage, recordEvent]);

  const startCamera = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: 320, height: 240 }, audio: false });
    streamRef.current = stream;
    if (webcamRef.current) webcamRef.current.srcObject = stream;
    setCameraActive(true);
  };

  const takeSnapshot = useCallback(async () => {
    const video = webcamRef.current;
    if (!video || !streamRef.current) { recordEvent("camera_off"); return; }
    try {
      const canvas = document.createElement("canvas");
      canvas.width = 320; canvas.height = 240;
      canvas.getContext("2d").drawImage(video, 0, 0, 320, 240);
      const b64 = canvas.toDataURL("image/jpeg", 0.5);
      await driveAssessmentSnapshot(driveId, b64);
    } catch (_) {}
  }, [driveId, recordEvent]);

  const cleanup = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setCameraActive(false);
    if (snapTimerRef.current) clearInterval(snapTimerRef.current);
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
  };

  const begin = async () => {
    setErr(null);
    try {
      await startCamera();               // camera is REQUIRED — throws if denied
    } catch (_) {
      setErr("Camera access is required for this proctored assessment. Allow the camera and try again.");
      return;
    }
    try {
      await document.documentElement.requestFullscreen();
    } catch (_) { /* fullscreen denial is recorded as violations later */ }
    try {
      const { data } = await startDriveAssessment(driveId);
      setTest(data); setAnswers({}); eventsRef.current = []; setViolations(0);
      setSecondsLeft(data.duration_min * 60);
      setStage("live");
      snapTimerRef.current = setInterval(takeSnapshot, SNAPSHOT_EVERY_MS);
      setTimeout(takeSnapshot, 3000);    // first snapshot early
    } catch (e) {
      cleanup();
      setErr(e?.response?.data?.detail || "Could not start the assessment.");
    }
  };

  const finish = useCallback(async () => {
    setStage("submitting");
    if (snapTimerRef.current) clearInterval(snapTimerRef.current);
    try {
      const { data } = await submitDriveAssessment(driveId, {
        answers, events: eventsRef.current.slice(0, 200), camera_active: streamRef.current !== null,
      });
      cleanup();
      setDoneMsg(data.message || "Submitted.");
      setStage("done");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Submit failed — your answers are kept; try again.");
      setStage("live");
    }
  }, [driveId, answers]); // eslint-disable-line

  useEffect(() => {
    if (stage !== "live") return;
    const t = setInterval(() => setSecondsLeft((s) => {
      if (s <= 1) { clearInterval(t); finish(); return 0; }
      return s - 1;
    }), 1000);
    return () => clearInterval(t);
  }, [stage, finish]);

  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, "0");
  const ss = String(secondsLeft % 60).padStart(2, "0");

  if (stage === "loading") return <div className="container-x py-16 text-stone-500 flex items-center gap-2"><Loader2 size={18} className="animate-spin" /> Loading assessment…</div>;

  if (stage === "error") return (
    <div className="container-x py-16 max-w-lg">
      <div className="rounded-3xl border border-stone-200 bg-white p-8 text-center">
        <Lock size={28} className="mx-auto text-stone-300" />
        <p className="mt-4 text-sm text-stone-600">{err}</p>
        <button onClick={() => navigate("/app/campus")} className="mt-5 inline-flex items-center gap-2 text-sm text-[#C2410C] font-medium"><ArrowLeft size={14} /> Back to drives</button>
      </div>
    </div>
  );

  if (stage === "done") return (
    <div className="container-x py-16 max-w-xl" data-testid="da-done">
      <div className="rounded-3xl border border-stone-200 bg-white p-10 text-center">
        <CheckCircle2 size={34} className="mx-auto text-emerald-500" />
        <h1 className="font-display text-2xl mt-4">{meta?.drive?.company_name} — {meta?.drive?.title}</h1>
        <p className="text-sm text-stone-600 mt-3">{doneMsg}</p>
        {meta?.result && (
          <div className="mt-6 rounded-2xl bg-stone-900 text-stone-50 p-6">
            <p className="font-display text-5xl">{meta.result.score}<span className="text-xl text-stone-400">/100</span></p>
            <p className={`text-sm mt-1 ${meta.result.clears_cutoff ? "text-emerald-400" : "text-red-400"}`}>
              {meta.result.clears_cutoff ? "Cutoff cleared" : "Below cutoff"}
            </p>
          </div>
        )}
        <button onClick={() => navigate("/app/campus")} className="mt-6 inline-flex items-center gap-2 text-sm text-[#C2410C] font-medium"><ArrowLeft size={14} /> Back to drives</button>
      </div>
    </div>
  );

  if (stage === "intro") return (
    <div className="container-x py-12 max-w-2xl" data-testid="da-intro">
      <span className="overline">Drive assessment · proctored</span>
      <h1 className="font-display text-3xl md:text-4xl mt-2">{meta.drive.company_name} — {meta.drive.title}</h1>
      <p className="text-sm text-stone-600 mt-2">{meta.template_name} · {meta.total_questions} questions · {meta.duration_min} minutes · window closes {new Date(meta.window_end).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</p>

      <div className="rounded-3xl border border-amber-200 bg-amber-50 p-6 mt-6">
        <h2 className="font-display text-lg flex items-center gap-2"><ShieldCheck size={18} className="text-amber-600" /> This is a real screening test — proctoring rules</h2>
        <ul className="mt-3 space-y-2 text-sm text-stone-700">
          <li className="flex gap-2"><Camera size={15} className="shrink-0 mt-0.5 text-amber-600" /> Camera must stay ON — periodic snapshots are taken and reviewed by an AI proctor (face count, second person, phone in frame).</li>
          <li className="flex gap-2"><Maximize size={15} className="shrink-0 mt-0.5 text-amber-600" /> The test runs in fullscreen. Exiting fullscreen or switching tabs is recorded on your report.</li>
          <li className="flex gap-2"><AlertTriangle size={15} className="shrink-0 mt-0.5 text-amber-600" /> Copy, paste and right-click are disabled and logged. Your placement cell sees a full violation timeline.</li>
          <li className="flex gap-2"><Clock size={15} className="shrink-0 mt-0.5 text-amber-600" /> One attempt only. Auto-submits when time ends. Results are announced by your placement cell.</li>
        </ul>
      </div>

      {err && <p className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-2">{err}</p>}
      <button onClick={begin} data-testid="da-start"
        className="mt-6 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-stone-900 text-white font-medium hover:bg-stone-700">
        <ShieldCheck size={16} /> I understand — start the proctored assessment
      </button>
    </div>
  );

  if (stage === "submitting") return (
    <div className="container-x py-16 text-center">
      <Loader2 size={28} className="animate-spin mx-auto text-[#C2410C]" />
      <p className="mt-4 font-display text-2xl">Submitting your assessment…</p>
      <p className="text-sm text-stone-500 mt-1">Grading and integrity review in progress.</p>
    </div>
  );

  // ── LIVE ──
  return (
    <div className="container-x py-8" data-testid="da-live">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6 items-start">
        <div className="space-y-6 select-none">
          <div className="rounded-2xl bg-stone-900 text-stone-50 px-5 py-3 flex items-center justify-between">
            <p className="text-sm font-medium">{test.drive.company_name} — {test.template_name}</p>
            <p className="text-xs text-stone-400">Answer all sections · auto-submits at 00:00</p>
          </div>
          {test.sections.map((s) => (
            <div key={s.key}>
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-display text-xl">{s.label}</h2>
                <span className="text-xs font-mono text-stone-400">{s.mcq.length}Q · suggested {s.minutes}m</span>
              </div>
              <div className="space-y-4">
                {s.mcq.map((q) => (
                  <div key={q.index} className="rounded-2xl border border-stone-200 bg-white p-5" data-testid={`da-q-${q.index}`}>
                    <p className="text-sm font-medium text-stone-900 whitespace-pre-wrap"><span className="text-stone-400 font-mono mr-2">Q{q.index + 1}</span>{q.q}</p>
                    <div className="mt-3 grid sm:grid-cols-2 gap-2">
                      {q.options.map((op, oi) => (
                        <label key={oi} className={`flex items-center gap-2.5 px-3 py-2 rounded-xl border text-sm cursor-pointer ${answers[String(q.index)] === oi ? "border-stone-900 bg-stone-50 font-medium" : "border-stone-200 hover:border-stone-400"}`}>
                          <input type="radio" name={`q${q.index}`} checked={answers[String(q.index)] === oi}
                            onChange={() => setAnswers((a) => ({ ...a, [String(q.index)]: oi }))} className="accent-[#C2410C]" />
                          {op}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          <button onClick={finish} data-testid="da-submit"
            className="w-full px-6 py-3.5 rounded-full bg-stone-900 text-white font-medium hover:bg-stone-700">
            Submit assessment
          </button>
        </div>

        {/* Proctor rail */}
        <div className="lg:sticky lg:top-6 space-y-3">
          <div className={`rounded-2xl p-4 text-center font-mono text-3xl font-bold ${secondsLeft < 120 ? "bg-red-50 text-red-600 border border-red-200" : "bg-stone-900 text-amber-300"}`} data-testid="da-timer">
            {mm}:{ss}
          </div>
          <div className="rounded-2xl border border-stone-200 bg-white p-3">
            <video ref={webcamRef} autoPlay muted playsInline className="w-full rounded-xl bg-stone-900 aspect-video object-cover" />
            <div className="mt-2 flex items-center justify-between text-xs">
              <span className={`inline-flex items-center gap-1 ${cameraActive ? "text-emerald-600" : "text-red-600"}`}>
                {cameraActive ? <Camera size={12} /> : <CameraOff size={12} />} {cameraActive ? "AI proctor on" : "Camera off"}
              </span>
              <span className={`inline-flex items-center gap-1 ${violations === 0 ? "text-emerald-600" : "text-red-600"}`}>
                {violations === 0 ? <ShieldCheck size={12} /> : <ShieldAlert size={12} />} {violations} violation{violations !== 1 ? "s" : ""}
              </span>
            </div>
          </div>
          <p className="text-[11px] text-stone-400 px-1">Snapshots every 30s. Every tab switch, fullscreen exit, copy/paste attempt and camera drop is timestamped on the report your placement cell sees.</p>
        </div>
      </div>
    </div>
  );
}

function statusMessage(data) {
  if (data.status === "submitted") return data.result ? "Results are published below." : "Your assessment is submitted. Results will be announced by your placement cell.";
  if (data.status === "closed") return "The assessment window has closed.";
  if (data.status === "in_progress") return "You have an attempt in progress — it will be auto-submitted after its timer.";
  return "";
}
